import {
	BaseModal,
	Button,
	CommonModalProps,
	Container,
	Grid,
	GridItem,
	Screen,
	useRespondTo,
} from "@animech/ui";
import { useTheme } from "@emotion/react";
import { FunctionComponent, HTMLAttributes } from "react";
import { FieldValueMeta } from "@animech/axiom-ui-builder";
import { getLocalizablePropertyValue } from "@animech/axiom-utility";
import * as Styled from "./field-info-modal.style";

import { useUiData } from "src/ui";
import { Property, PropertyValue } from "src/common";
import { assertIsLocalizable } from "src/utility";

type CoreFieldInfoModalProps = CommonModalProps<
	HTMLAttributes<HTMLDivElement>
> & {
	imageSrc?: string;
	fullWidthImage?: boolean;
	texts: {
		titleOverline?: string;
		title: string;
		body?: string;
		primaryAction?: string;
		secondaryAction?: string;
	};
	onPrimaryAction?: () => void;
	onSecondaryAction?: () => void;
};

const CoreFieldInfoModal: FunctionComponent<CoreFieldInfoModalProps> = ({
	imageSrc,
	fullWidthImage,
	texts,
	onPrimaryAction,
	onSecondaryAction,
	...commonProps
}) => {
	const { maxWidth } = useTheme().app.modals;
	const isMobile = useRespondTo("mobile");

	const body = (
		<Styled.Root data-full-width-image={fullWidthImage || undefined}>
			{imageSrc && (
				<Styled.ImageContainer>
					<Styled.Image src={imageSrc} />
				</Styled.ImageContainer>
			)}
			<Styled.TextContainer>
				{texts.titleOverline && (
					<Styled.Overline>{texts.titleOverline}</Styled.Overline>
				)}
				<Styled.Heading>{texts.title}</Styled.Heading>
				{texts.body && <Styled.Body>{texts.body}</Styled.Body>}
			</Styled.TextContainer>
		</Styled.Root>
	);

	const footer =
		onSecondaryAction || onPrimaryAction ? (
			<Container>
				<Grid spacing="medium">
					{onSecondaryAction && (
						<GridItem rootAttributes={{ style: { textAlign: "left" } }}>
							<Button
								type="auto"
								appearance="ghost"
								color="neutral"
								onClick={onSecondaryAction}
							>
								{texts.secondaryAction}
							</Button>
						</GridItem>
					)}
					{onPrimaryAction && (
						<GridItem rootAttributes={{ style: { textAlign: "right" } }}>
							<Button type="auto" onClick={onPrimaryAction}>
								{texts.primaryAction}
							</Button>
						</GridItem>
					)}
				</Grid>
			</Container>
		) : null;

	return isMobile ? (
		<Screen {...commonProps} footer={footer}>
			{body}
		</Screen>
	) : (
		<BaseModal {...commonProps} maxWidth={maxWidth} footer={footer}>
			{body}
		</BaseModal>
	);
};

export type FieldInfoModalProps = CommonModalProps<
	HTMLAttributes<HTMLDivElement>
> & {
	property: Property;
	imageSrc?: string;
};

export const FieldInfoModal: FunctionComponent<FieldInfoModalProps> = ({
	property,
	...commonProps
}) => {
	const { localizer } = useUiData();
	assertIsLocalizable(property);
	return (
		<CoreFieldInfoModal
			{...commonProps}
			fullWidthImage
			texts={{
				titleOverline: localizer.localizeProperty(property, "overline"),
				title: localizer.localizeProperty(property, "label"),
				body: localizer.localizeProperty(property, "info"),
			}}
		/>
	);
};

export type FieldValueInfoModalProps = CommonModalProps<
	HTMLAttributes<HTMLDivElement>
> & {
	property: Property;
	fieldValue: PropertyValue;
	fieldValueMeta: FieldValueMeta;
	onSelect?: (property: Property, fieldValue: unknown) => void;
	onMoreInfo?: (property: Property, fieldValue: unknown) => void;
};

export const FieldValueInfoModal: FunctionComponent<
	FieldValueInfoModalProps
> = ({
	property,
	fieldValue,
	fieldValueMeta,
	onMoreInfo,
	onSelect,
	...commonProps
}) => {
	assertIsLocalizable(property);
	const { localizer } = useUiData();
	const value = getLocalizablePropertyValue(fieldValue);
	return (
		<CoreFieldInfoModal
			{...commonProps}
			imageSrc={fieldValueMeta.image}
			texts={{
				titleOverline: localizer.localizeProperty(property),
				title: localizer.localizePropertyValue(property, value) ?? value,
				body: localizer.localizePropertyValue(property, value, "info"),
				primaryAction: localizer.localizeTerm("propertyInfoModalSelect"),
				secondaryAction: localizer.localizeTerm("propertyInfoModalMoreInfo"),
			}}
			onPrimaryAction={
				onSelect ? () => onSelect(property, fieldValue) : undefined
			}
			onSecondaryAction={
				onMoreInfo ? () => onMoreInfo(property, fieldValue) : undefined
			}
		/>
	);
};
